import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { SourceBadge, RiskBadge } from "@/components/SourceBadge";
import { SummaryText } from "@/components/SummaryText";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Buildings, User, Sparkle, Scales, Gavel, Newspaper, Bank,
  ArrowLeft, ShieldCheck, FunnelSimple, Hash,
} from "@phosphor-icons/react";

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-zinc-200 rounded-sm">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-zinc-200">
        <Icon size={16} weight="duotone" className="text-zinc-500" />
        <h3 className="font-heading text-sm font-bold text-zinc-950 uppercase tracking-wide">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Field({ label, item }) {
  if (!item) return null;
  const value = typeof item === "object" ? item.value : item;
  const source = typeof item === "object" ? item.source : null;
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-zinc-100 last:border-0">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wide text-zinc-400">{label}</div>
        <div className="text-sm text-zinc-900 font-medium mt-0.5">{value}</div>
      </div>
      {source && <div className="shrink-0 pt-3"><SourceBadge source={source} /></div>}
    </div>
  );
}

export default function Report() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/reports/${id}`).then((r) => setReport(r.data)).catch(() => setReport(false)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <AppShell breadcrumb="Report"><div className="text-sm text-zinc-500">Caricamento…</div></AppShell>;
  if (!report) return <AppShell breadcrumb="Report"><div className="text-sm text-zinc-500">Report non trovato.</div></AppShell>;

  const d = report.dossier;
  const isCorp = report.entity_type === "corporate";

  const download = () => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `certytrace-report-${report.target_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppShell breadcrumb="Report">
      <div className="max-w-5xl">
        <button onClick={() => navigate(-1)} data-testid="report-back" className="flex items-center gap-1 text-xs font-mono uppercase tracking-wide text-zinc-500 hover:text-zinc-900 mb-4">
          <ArrowLeft size={14} /> Indietro
        </button>

        {/* Header */}
        <div className="bg-white border border-zinc-950 rounded-sm p-5 mb-4" data-testid="report-header">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-zinc-950 flex items-center justify-center rounded-sm">
                {isCorp ? <Buildings size={22} weight="duotone" className="text-white" /> : <User size={22} weight="duotone" className="text-white" />}
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  {isCorp ? "Report Società" : "Report Persona · Contesto Professionale"}
                </div>
                <h1 className="font-heading text-2xl font-extrabold tracking-tight text-zinc-950" data-testid="report-target-name">{report.target_name}</h1>
                <div className="text-xs font-mono text-zinc-400">{report.target_id}</div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <RiskBadge level={report.risk_level} />
              {report.mocked === false && (
                <span data-testid="report-source-badge" className="inline-flex items-center gap-1 border border-emerald-500 bg-emerald-50 text-emerald-700 text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 rounded-sm">
                  <ShieldCheck size={11} weight="fill" /> Live · Registro Imprese
                </span>
              )}
              <Button onClick={download} data-testid="report-download" variant="outline" size="sm" className="rounded-sm border-zinc-300 text-xs">
                Scarica report (JSON)
              </Button>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap border-t border-zinc-100 pt-3">
            <span className="text-[10px] font-mono uppercase tracking-wide text-zinc-400">Finalità (gate):</span>
            <span className="inline-flex items-center gap-1 border border-zinc-300 bg-zinc-100 text-zinc-700 text-[11px] px-2 py-0.5 rounded-sm">
              <Scales size={12} /> {report.business_purpose_label}
            </span>
            {report.justification_text && (
              <span className="text-xs text-zinc-500 italic">“{report.justification_text}”</span>
            )}
          </div>
        </div>

        {/* AI summary */}
        <div className="bg-zinc-950 text-white rounded-sm p-5 mb-4" data-testid="report-ai-summary">
          <div className="flex items-center gap-2 mb-3">
            <Sparkle size={16} weight="fill" className="text-white" />
            <h3 className="font-heading text-sm font-bold uppercase tracking-wide">Sintesi AI · Gemini 3.1 Pro</h3>
          </div>
          <div className="[&_strong]:text-white [&_p]:text-zinc-300 [&_li]:text-zinc-300 avz-summary text-sm leading-relaxed">
            <SummaryText text={report.ai_summary} />
          </div>
        </div>

        {/* Dossier grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {isCorp ? (
            <>
              <Section icon={Bank} title="Stato & Anagrafica" testid="section-legal-status">
                <Field label="Stato giuridico" item={d.stato_giuridico} />
                <Field label="Forma giuridica" item={d.forma_giuridica} />
                <Field label="Data costituzione" item={d.data_costituzione} />
                <Field label="Capitale sociale" item={d.capitale_sociale} />
                <Field label="Sede legale" item={d.sede_legale} />
                <Field label="Settore" item={d.settore} />
              </Section>

              <Section icon={User} title="Organo Amministrativo" testid="section-board">
                {(d.consiglio_amministrazione || []).map((m, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 py-2 border-b border-zinc-100 last:border-0">
                    <div>
                      <div className="text-sm font-medium text-zinc-900">{m.nome}</div>
                      <div className="text-xs text-zinc-500">{m.carica} · dal {m.dal}</div>
                    </div>
                    <SourceBadge source={m.source} />
                  </div>
                ))}
              </Section>

              <Section icon={Bank} title="Dati Economici" testid="section-financials">
                {(d.financials || []).map((f, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-zinc-100 last:border-0">
                    <div className="font-mono text-sm text-zinc-900">{f.anno}</div>
                    <div className="flex gap-4 text-xs text-zinc-600">
                      <span>Ricavi <b className="text-zinc-900">{f.ricavi}</b></span>
                      <span>EBITDA <b className="text-zinc-900">{f.ebitda}</b></span>
                      <span className={f.utile_netto?.includes("-") ? "text-red-600" : ""}>Utile <b>{f.utile_netto}</b></span>
                    </div>
                    <SourceBadge source={f.source} />
                  </div>
                ))}
              </Section>

              <Section icon={Newspaper} title="Avvisi Legali (Gazzetta)" testid="section-gazzetta">
                {(d.gazzetta_notices || []).length === 0 ? (
                  <div className="text-sm text-zinc-400 italic">Nessun avviso rilevante.</div>
                ) : (
                  (d.gazzetta_notices || []).map((g, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 py-2 border-b border-zinc-100 last:border-0">
                      <div>
                        <div className="text-sm font-medium text-zinc-900 flex items-center gap-2">
                          {g.tipo} {g.risk && g.risk !== "safe" && <RiskBadge level={g.risk} />}
                        </div>
                        <div className="text-xs text-zinc-500">{g.data} · {g.esito}</div>
                      </div>
                      <SourceBadge source={g.source} />
                    </div>
                  ))
                )}
              </Section>

              <Section icon={Gavel} title="Contenzioso (Tribunali)" testid="section-litigation">
                {(d.litigation || []).length === 0 ? (
                  <div className="text-sm text-zinc-400 italic">Nessun procedimento rilevato.</div>
                ) : (
                  (d.litigation || []).map((l, i) => (
                    <div key={i} className="py-2 border-b border-zinc-100 last:border-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-sm font-medium text-zinc-900">{l.tribunale} · {l.ruolo}</div>
                        <SourceBadge source={l.source} />
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">{l.materia} — {l.parti}</div>
                      <div className="mt-1"><RiskBadge level={l.risk} /> <span className="text-xs text-zinc-500 ml-1">{l.stato}</span></div>
                    </div>
                  ))
                )}
              </Section>
            </>
          ) : (
            <>
              <Section icon={User} title="Profilo Professionale" testid="section-profile">
                <Field label="Professione" item={d.professione} />
              </Section>

              <Section icon={Buildings} title="Cariche & Directorships" testid="section-directorships">
                {(d.directorships || []).map((m, i) => (
                  <div key={i} className="py-2 border-b border-zinc-100 last:border-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-zinc-900">{m.azienda}</div>
                        <div className="text-xs text-zinc-500">{m.carica} · dal {m.dal} · {m.stato}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <SourceBadge source={m.source} />
                        {m.risk && m.risk !== "safe" && <RiskBadge level={m.risk} />}
                      </div>
                    </div>
                  </div>
                ))}
              </Section>

              <Section icon={ShieldCheck} title="Iscrizioni Professionali" testid="section-registrations">
                {(d.professional_registrations || []).length === 0 ? (
                  <div className="text-sm text-zinc-400 italic">Nessuna iscrizione ad albi rilevata.</div>
                ) : (
                  (d.professional_registrations || []).map((r, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 py-2 border-b border-zinc-100 last:border-0">
                      <div>
                        <div className="text-sm font-medium text-zinc-900">{r.albo}</div>
                        <div className="text-xs text-zinc-500">N. {r.numero} · {r.stato}</div>
                      </div>
                      <SourceBadge source={r.source} />
                    </div>
                  ))
                )}
              </Section>

              <Section icon={Gavel} title="Contenzioso (Tribunali)" testid="section-litigation">
                {(d.litigation || []).length === 0 ? (
                  <div className="text-sm text-zinc-400 italic">Nessun procedimento rilevato.</div>
                ) : (
                  (d.litigation || []).map((l, i) => (
                    <div key={i} className="py-2 border-b border-zinc-100 last:border-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-sm font-medium text-zinc-900">{l.tribunale} · {l.ruolo}</div>
                        <SourceBadge source={l.source} />
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">{l.materia}</div>
                      <div className="mt-1"><RiskBadge level={l.risk} /> <span className="text-xs text-zinc-500 ml-1">{l.stato}</span></div>
                    </div>
                  ))
                )}
              </Section>
            </>
          )}
        </div>

        {/* Compliance footer */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="report-filter-notice">
            <div className="flex items-center gap-2 mb-1">
              <FunnelSimple size={15} weight="duotone" className="text-zinc-500" />
              <span className="text-xs font-mono uppercase tracking-wide text-zinc-600">Filtro di conformità server-side</span>
            </div>
            <p className="text-xs text-zinc-500">
              Categorie sensibili (salute, politica, religione, vita sessuale) e PII non professionali sono
              bloccate a livello server. Campi rimossi: <b className="text-zinc-800">{report.filtered_fields?.length || 0}</b>.
            </p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm p-4" data-testid="report-ledger-ref">
            <div className="flex items-center gap-2 mb-1">
              <Hash size={15} weight="duotone" className="text-zinc-500" />
              <span className="text-xs font-mono uppercase tracking-wide text-zinc-600">Riferimento Audit Ledger</span>
            </div>
            <p className="text-xs text-zinc-500 font-mono break-all">
              {report.ledger_entry_hash}
            </p>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
